
import { Refresh } from '@material-ui/icons';
import { AutoComplete, Button, Dropdown, SmSpace, TextButton } from 'm2m.components';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { CircularProgress, Grid } from '@material-ui/core';
import AWS from 'aws-sdk';
import { DataGridPro, GridCellParams, GridSortDirection, GridSortModel, GridValueGetterParams, GridComparatorFn } from '@mui/x-data-grid-pro';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import DownloadIcon from '@material-ui/icons/GetApp';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import { FileResponse, FuncaoParticipante, GrupoAcesso, ParticipanteResponse, RegistroStatus } from 'common/genericTypes';
import { Option } from 'common/genericOptions';
import _ from 'lodash';
import { ArquivoRequest, ArquivosMultiplos, BatchFileDownloadResponse } from 'common/types/arquivosMultiplos';
import moment from 'moment-timezone';
import JSZip from 'jszip';

const DownloadDocsS3: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [downloadingBatch, setDownloadingBatch] = useState<boolean>(false);
  const [formattedData, setFormattedData] = useState<Array<FileResponse>>([])
  const s3 = new AWS.S3();
  const [pageSize, setPageSize] = React.useState(20);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'dataResponse',
      sort: 'desc' as GridSortDirection,
    },
  ]);

  const usuarioLogado = useSelector((state: RootState) => state.auth.usuario.participante);
  const isParticipanteCustodiante = useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Custodiante;
  const isParticipanteEscriturador = useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Escriturador;
  const isUsuarioOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes; 
  const isUsuarioCadastroOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.CadastroOperacoes; 
  const notaComercialByFileApi = useSelector((state: RootState) => state.notaComercialByFileApi);

  const usuarioLogadoEmail = useSelector((state: RootState) => state.auth.usuario.email);
  const usuarioLogadoParticipanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const participanteFuncaoUsuarioId = useSelector((state: RootState) => state.auth.usuario.participanteFuncaoUsuarioId);

  const dispatch = useDispatch();

  const getParticipantesResume = useSelector((state: RootState) => state.participanteApi.getParticipantesResume);
  const [participantes, setParticipantes] = useState<ParticipanteResponse[]>([]);
  const [participanteOptions, setParticipanteOptions] = useState<Option[]>([]);
  const [selectedParticipante, setSelectedParticipante] = useState<Option>({ value: '', description: '' });
  const [funcaoOptions, setFuncaoOptions] = useState<Option[]>([]);
  const [selectedFuncao, setSelectedFuncao] = useState<string>('');

  const loadParticipantes = useCallback(async () => {
    try {
      setLoading(true);
      if (isUsuarioOperacoes || isUsuarioCadastroOperacoes) {
        const result = await getParticipantesResume(dispatch, { 
          funcoes: [FuncaoParticipante.Custodiante, FuncaoParticipante.Escriturador], 
          suspenso: false, 
          status: RegistroStatus.Aprovado 
        }, true);
        setParticipantes(result);
        const options = result.map((p) => ({
          value: p.id,
          description: p.razaoSocial,
        }));
        setParticipanteOptions(options);
        setLoading(false);
      } else {
        fetchFiles();
      }
    } catch (error) {
      setLoading(false);
    }
  }, [dispatch, getParticipantesResume, isUsuarioOperacoes, isUsuarioCadastroOperacoes]);

  const handleParticipanteChange = useCallback((value: Option | null) => {
    const safeValue = value || { value: '', description: '' };
    setSelectedParticipante(safeValue);
    setSelectedFuncao('');
    
    if (safeValue.value) {
      const participante = participantes.find((p) => p.id === safeValue.value);
      if (participante && participante.funcoes) {
        const funcoesValidas = participante.funcoes.filter(
          (f) => f.funcao === FuncaoParticipante.Custodiante || f.funcao === FuncaoParticipante.Escriturador
        );
        const options = funcoesValidas.map((f) => ({
          value: f.participanteFuncaoId || '',
          description: f.funcao === FuncaoParticipante.Custodiante ? 'Custodiante' : 'Escriturador',
        }));
        setFuncaoOptions(options);
      } else {
        setFuncaoOptions([]);
      }
    } else {
      setFuncaoOptions([]);
    }
  }, [participantes]);

  const handleBuscarArquivos = useCallback(() => {
    if (selectedParticipante?.value && selectedFuncao) {
      fetchFiles();
    }
  }, [selectedParticipante, selectedFuncao]);  

  const getTipoParticipanteDir = useCallback(() => {
    if (isParticipanteCustodiante) return 'custodiante';
    if (isParticipanteEscriturador) return 'escriturador';
    
    const funcaoSelecionada = funcaoOptions.find((f) => f.value === selectedFuncao);
    if (funcaoSelecionada) {
      return funcaoSelecionada.description.toLowerCase();
    }
    return '';
  }, [isParticipanteCustodiante, isParticipanteEscriturador, funcaoOptions, selectedFuncao]);

  const getParticipanteIdParaBusca = useCallback(() => {
    if (isParticipanteCustodiante || isParticipanteEscriturador) {
      return usuarioLogado.id.toString();
    }
    return selectedParticipante?.value || '';
  }, [isParticipanteCustodiante, isParticipanteEscriturador, usuarioLogado.id, selectedParticipante]);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const tipoDir = getTipoParticipanteDir();
      const participanteId = getParticipanteIdParaBusca();

      if (!tipoDir || (!isParticipanteCustodiante && !isParticipanteEscriturador && !participanteId)) {
        setLoading(false);
        return;
      }

      const params = () => (isParticipanteCustodiante || isParticipanteEscriturador) ? {
        diretorio: `${usuarioLogado.id.toString()}/${tipoDir}`,
        bucket: process.env['REACT_APP_AWS_BUCKET_SFTP'] || '',
      } : {
        diretorio: `${participanteId}/${tipoDir}`,
        bucket: process.env['REACT_APP_AWS_BUCKET_SFTP'] || '',
      };
  
      const getNotasComerciais = await notaComercialByFileApi.getAllNotasComerciaisByFile(dispatch, params(), true);
      let counter = 0;
      let informacoes: FileResponse[] = [];
  
      getNotasComerciais.forEach((arr) => {
        const keys = arr.diretorio.split("/");
        if (keys.length > 2 && (keys[1] === "custodiante" || keys[1] === "escriturador")) {
          const participanteIdArquivo = keys[0];
          const tipoParticipante = keys[1];
          
          let razaoParticipante: string | undefined;
          if (isParticipanteCustodiante || isParticipanteEscriturador) {
            razaoParticipante = usuarioLogado.razaoSocial;
          } else {
            const part = participantes.find((p) => p.id === participanteIdArquivo);
            razaoParticipante = part?.razaoSocial;
          }

          const isParticipanteLogado = participanteIdArquivo === usuarioLogado.id.toString();
          const isParticipanteSelecionado = participanteIdArquivo === selectedParticipante?.value;
          const tipoArquivo = keys[3];

          if ((isParticipanteCustodiante || isParticipanteEscriturador) && isParticipanteLogado) {
            informacoes.push({
              id: counter++,
              razaoCustodiante: razaoParticipante,
              nomeArquivo: arr.nomeArquivo,
              tipoArquivo: tipoArquivo,
              dataResponse: arr.dataModificacao,
              fileResponse: `${arr.diretorio}/${arr.nomeArquivo}`,
            });
          } else if (!isParticipanteCustodiante && !isParticipanteEscriturador && isParticipanteSelecionado) {
            informacoes.push({
              id: counter++,
              razaoCustodiante: razaoParticipante,
              nomeArquivo: arr.nomeArquivo,
              tipoArquivo: tipoArquivo,
              dataResponse: arr.dataModificacao,
              fileResponse: `${arr.diretorio}/${arr.nomeArquivo}`,
            });
          }
        }
      });
  
      setFormattedData(informacoes);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching files: ', error);
      setLoading(false);
    }
  }, [dispatch, notaComercialByFileApi, usuarioLogado, isParticipanteCustodiante, isParticipanteEscriturador, participantes, selectedParticipante, getTipoParticipanteDir, getParticipanteIdParaBusca]);

  const handleDownload = useCallback(async (fileResponse: string, fileName: string) => {
    if (fileResponse && fileName) {
      const params = {
        diretorio: fileResponse.split('/').slice(0, -1).join('/') || '',
        bucket: process.env['REACT_APP_AWS_BUCKET_SFTP'] || '',
        nomeArquivo: fileResponse.split('/').slice(-1)[0] || '',
      };
      
      const getFiles = await notaComercialByFileApi.getFileNotaComercialByFile(dispatch, params, true);
  
      if (getFiles && getFiles.arquivo !== null) {
        const file = getFiles.arquivo;
        const blob = new Blob([file], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
      }
    }
  }, []);

  const getNewestFiles = useCallback(() => {
    if (formattedData.length === 0) return [];
    
    const sorted = [...formattedData].sort((a, b) => {
      return moment(b.dataResponse).diff(moment(a.dataResponse));
    });
    
    const newestDate = moment(sorted[0].dataResponse).startOf('day');
    
    const newestFiles = formattedData.filter(file => {
      const fileDate = moment(file.dataResponse).startOf('day');
      return fileDate.isSame(newestDate);
    });
    
    return newestFiles;
  }, [formattedData]);

  const getNewestFilesByType = useCallback(() => {
    const newestFiles = getNewestFiles();
    const filesByType: Record<string, FileResponse[]> = {};
    
    newestFiles.forEach((file: FileResponse) => {
      const fileType = file.tipoArquivo || 'outros';
      
      if (!filesByType[fileType]) {
        filesByType[fileType] = [];
      }
      filesByType[fileType].push(file);
    });
    
    return filesByType;
  }, [getNewestFiles]);

const handleDownloadNewestFiles = useCallback(async () => {
  if (downloadingBatch) return;

  const filesByType = getNewestFilesByType();
  const fileTypes = Object.keys(filesByType);

  if (fileTypes.length === 0) {
    dispatch(
      addAlert({
        message: 'Não há arquivos disponíveis para download.',
        severity: 'warning',
      })
    );
    return;
  }

  const arquivos: ArquivoRequest[] = [];
  
  for (const type of fileTypes) {
    filesByType[type].forEach((file: FileResponse) => {
      if (file.fileResponse) {
        arquivos.push({
          Diretorio: file.fileResponse.split("/").slice(0, -1).join("/") || "",
          NomeArquivo: file.fileResponse.split("/").slice(-1)[0] || "",
          DataModificacao: file.dataResponse,
          BucketName: process.env["REACT_APP_AWS_BUCKET_SFTP"] || "",
        });
      }
    });
  }

  if (arquivos.length === 0) {
    dispatch(
      addAlert({
        message: 'Não foi possível preparar os arquivos para download.',
        severity: 'error',
      })
    );
    return;
  }

  try {
    setDownloadingBatch(true);

    const params: ArquivosMultiplos = {
      Arquivos: arquivos,
      Bucket: process.env["REACT_APP_AWS_BUCKET_SFTP"] || "",
      ParticipanteId: isParticipanteCustodiante
        ? usuarioLogadoParticipanteId
        : undefined,
    };

    const response = (await notaComercialByFileApi.downloadMultipleFiles(
      dispatch,
      params,
      true
    )) as unknown as BatchFileDownloadResponse[];

    if (!response || response.length === 0) {
      throw new Error("Servidor retornou uma lista vazia de arquivos");
    }

    if (response.length === 1) {
      const file = response[0];
      const blob = new Blob([file.arquivo], { type: "text/plain" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = file.nomeArquivo || `arquivo_${moment().format("YYYYMMDD_HHmmss")}.txt`;
      link.click();
      URL.revokeObjectURL(link.href);

      dispatch(
        addAlert({
          message: 'Download concluído com sucesso!',
          severity: 'success',
        })
      );
      return;
    }

    const zip = new JSZip();
    const agora = new Date();
    const dataAtualBrasilia = new Date(agora.getTime() - (3 * 60 * 60 * 1000));
    
    response.forEach((file, index) => {
      let fileName = file.nomeArquivo || `arquivo_${index + 1}.txt`;
      
      if (fileName.includes('.') && !fileName.endsWith('.txt')) {
        fileName = fileName.substring(0, fileName.lastIndexOf('.')) + '.txt';
      } else if (!fileName.includes('.')) {
        fileName += '.txt';
      }
      
      zip.file(fileName, file.arquivo, {
        date: dataAtualBrasilia
      });
    });

    const zipContent = await zip.generateAsync({ type: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(zipContent);
    link.download = `arquivos_${moment().format("YYYYMMDD_HHmmss")}.zip`;
    link.click();
    URL.revokeObjectURL(link.href);

    dispatch(
      addAlert({
        message: 'Download de múltiplos arquivos concluído com sucesso!',
        severity: 'success',
      })
    );
  } catch (error) {
    console.error("Erro ao baixar os arquivos:", error);
    dispatch(
      addAlert({
        message: `Erro ao baixar os arquivos: ${error instanceof Error ? error.message : "Erro desconhecido"}`,
        severity: 'error',
      })
    );
  } finally {
    setDownloadingBatch(false);
  }
}, [
  getNewestFilesByType,
  dispatch,
  downloadingBatch,
  isParticipanteCustodiante,
  participanteFuncaoUsuarioId,
  notaComercialByFileApi,
]);

  useEffect(() => {
    loadParticipantes();
  }, []);

  const dateComparator: GridComparatorFn<Date> = (v1: Date, v2: Date) => moment(v1).diff(v2);

  const startCustomerX = () => {
    setTimeout(()=>{
        // @ts-ignore
        cx.survey('authorize', process.env['REACT_APP_CUSTOMERX_KEY']);
        // @ts-ignore
        cx.survey('identify', {external_id_client: usuarioLogadoParticipanteId, email: usuarioLogadoEmail});
        // @ts-ignore
        cx.survey('start', 'nps');
    },5000)
  }

  useEffect(() => {
    startCustomerX();
  }, []);

  const getGridColumns = useCallback(
    () => [
      {
        field: 'razaoCustodiante',
        headerName: 'Razão Custodiante',
        type: 'string',
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 100,
        maxWidth: 200,
        onColumnWidthChange: true,
        valueGetter: (params: GridValueGetterParams) => (params.row as FileResponse).razaoCustodiante,
      },
      {
        field: 'nomeArquivo',
        headerName: 'Nome Arquivo',
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => (params.row as FileResponse).nomeArquivo,
        width: 200,
        minWidth: 100,
        maxWidth: 300,
      },
      {
        field: 'dataResponse',
        headerName: 'Data Arquivo',
        filterable: true,
        sortable: true,
        type: 'date',
        sortComparator: dateComparator,
        valueGetter: (params: GridValueGetterParams) => (params.row as FileResponse).dataResponse,
        renderCell: (params: GridCellParams) => {
          return (params.row as FileResponse).dataResponse
            ? moment((params.row as FileResponse).dataResponse).format('DD/MM/YYYY, HH:mm:ss')
            : '';
        },
        width: 220,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'tipoArquivo',
        headerName: 'Tipo do Arquivo',
        filterable: true,
        sortable: true,
        disableExport: false,
        valueGetter: (params: GridValueGetterParams) => (params.row as FileResponse).tipoArquivo,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
      },
      {
        field: 'file_response_download',
        headerName: 'Download do Arquivo',
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => (params.row as FileResponse).fileResponse,
        renderCell: (params: GridCellParams) => {
          const response = params.row as FileResponse;
          return (
            <>
              <TextButton onClick={() => { handleDownload(response.fileResponse || "", response.nomeArquivo || "") }}>
                {'Download'}
                <DownloadIcon style={{ marginLeft: 10 }} fontSize={'small'} />
              </TextButton>
            </>
          );
        },
        width: 200,
        minWidth: 150,
        maxWidth: 200,
      },
    ],
    []
  );

  return (
    <Fragment>
      <Grid container spacing={2} alignItems="center">
        {(isUsuarioOperacoes || isUsuarioCadastroOperacoes) && (
          <>
            <Grid item>
              <AutoComplete
                label={'Participante'}
                value={selectedParticipante}
                onChange={(value) => handleParticipanteChange(value)}
                options={_.orderBy(participanteOptions, (x) => x.description)}
                filterOptions={(options: Array<Option>, state) => options.filter((_) => _.description.toLocaleLowerCase().includes(state.inputValue.toLocaleLowerCase()))}
                getOptionLabel={(option: Option) => option.description}
                style={{ width: '300px' }}
              />
            </Grid>
            <Grid item>
              <Dropdown
                label={'Função'}
                value={selectedFuncao}
                onChange={(value) => setSelectedFuncao(value)}
                options={funcaoOptions}
                style={{ width: '200px' }}
                disabled={!selectedParticipante?.value}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                color="primary"
                disabled={!selectedParticipante?.value || !selectedFuncao}
                onClick={handleBuscarArquivos}>
                {'Buscar Arquivos'}
              </Button>
            </Grid>
          </>
        )}
        <Grid item>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => (isParticipanteCustodiante || isParticipanteEscriturador) ? fetchFiles() : loadParticipantes()}>
            <Refresh fontSize="small" style={{ marginRight: '10px' }} />
            {'Recarregar'}
          </Button>
        </Grid>
        {(isParticipanteCustodiante || isParticipanteEscriturador) && (
          <Grid item>
            <Button
              variant="contained"
              size="small"
              color="primary"
              disabled={downloadingBatch}
              onClick={handleDownloadNewestFiles} >
              {downloadingBatch ? (
                <>
                  <CircularProgress size={16} color="inherit" style={{ marginRight: '10px' }} />
                  {'Processando download...'}
                </>
              ) : (
                <>
                  <CloudDownloadIcon fontSize="small" style={{ marginRight: '10px' }} />
                  {'Baixar arquivos diários'}
                </>
              )}
            </Button>
          </Grid>
        )}
      </Grid>
      <SmSpace />
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={formattedData}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        pagination
        onPageSizeChange={(newPage) => setPageSize(newPage)}
        pageSize={pageSize}
        loading={loading}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
      />
    </Fragment>
  );
};

export default DownloadDocsS3;
